import React, { useState, useEffect } from "react";
import {
  Copy,
  Star,
  Type,
  Download,
  Bold,
  Italic,
  Underline,
  CaseSensitive,
  Search,
  ALargeSmall,
} from "lucide-react";
import decorationItems from "../../components/FancyTextGeneratorToolComponents/decorationItems";
import Modal from "../../components/FancyTextGeneratorToolComponents/DecorationModal";
import html2canvas from "html2canvas";
import { X } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFont } from "@fortawesome/free-solid-svg-icons";
import fancyTextStyles from "../../components/FancyTextGeneratorToolComponents/fancyTextStyles";

const FancyTextGenerator = () => {
  const { darkMode } = useTheme();
  const [inputText, setInputText] = useState("");
  const [fancyTexts, setFancyTexts] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFancyText, setSelectedFancyText] = useState("");
  const [selectedStyle, setSelectedStyle] = useState(0);
  const [textStyles, setTextStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
    uppercase: false,
    lowercase: false,
    capitalize: false,
  });
  const [favorites, setFavorites] = useState([]);
  const [characterCount, setCharacterCount] = useState(0);
  const [previewText, setPreviewText] = useState("");
  const [styleSearchQuery, setStyleSearchQuery] = useState("");
  const [showStyleModal, setShowStyleModal] = useState(false);

  useEffect(() => {
    const savedFavorites = localStorage.getItem("fancyTextFavorites");
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  useEffect(() => {
    setCharacterCount(inputText.length);
  }, [inputText]);

  useEffect(() => {
    setPreviewText(generatePreviewText(inputText));
  }, [inputText, textStyles, selectedStyle]);

  const generateFancyTexts = (text) => {
    if (!text.trim()) {
      setFancyTexts([]);
      return;
    }

    const newFancyTexts = fancyTextStyles.map((style) => {
      let result = style(text);

      if (textStyles.bold) result = `**${result}**`;
      if (textStyles.italic) result = `*${result}*`;
      if (textStyles.underline) result = `_${result}_`;
      if (textStyles.uppercase) result = result.toUpperCase();
      if (textStyles.lowercase) result = result.toLowerCase();
      if (textStyles.capitalize) {
        result = result
          .split(" ")
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" ");
      }

      return result;
    });

    setFancyTexts(newFancyTexts);
    setCopiedIndex(null);
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    generateFancyTexts(text);
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleDecorateClick = (text) => {
    setSelectedFancyText(text);
    setIsModalOpen(true);
  };

  const toggleFavorite = (text) => {
    const newFavorites = favorites.includes(text)
      ? favorites.filter((fav) => fav !== text)
      : [...favorites, text];
    setFavorites(newFavorites);
    localStorage.setItem("fancyTextFavorites", JSON.stringify(newFavorites));
  };

  const downloadAsImage = async (text) => {
    const element = document.createElement("div");
    element.style.fontSize = "24px";
    element.style.padding = "20px";
    element.style.backgroundColor = "#ffffff";
    element.style.color = "#000000";
    element.textContent = text;
    document.body.appendChild(element);

    try {
      const canvas = await html2canvas(element);
      const link = document.createElement("a");
      link.download = "fancy-text.png";
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error("Error generating image:", error);
    }

    document.body.removeChild(element);
  };

  const handleStyleChange = (style) => {
    setTextStyles((prev) => {
      const newStyle = { ...prev };
      newStyle[style] = !prev[style];
      if (["uppercase", "lowercase", "capitalize"].includes(style)) {
        ["uppercase", "lowercase", "capitalize"].forEach((s) => {
          if (s !== style) newStyle[s] = false;
        });
      }
      return newStyle;
    });
    generateFancyTexts(inputText);
  };

  const generatePreviewText = (text) => {
    if (!text.trim()) return "";
    let result = fancyTextStyles[selectedStyle](text);

    if (textStyles.uppercase) result = result.toUpperCase();
    else if (textStyles.lowercase) result = result.toLowerCase();
    else if (textStyles.capitalize) {
      result = result
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    }

    return result;
  };

  const filteredStyles = fancyTextStyles
    .map((style, index) => ({
      index,
      preview: style("Preview Text")
    }))
    .filter(({ preview }) =>
      preview.toLowerCase().includes(styleSearchQuery.toLowerCase())
    );

  return (
    <>
      <HeaderSection toolId="fancyTextGenerator" />
      <main className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <article
          className="bg-white dark:bg-gray-800 rounded-xl dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white dark:bg-gray-800 dark:shadow-gray-900/10 dark:border-gray-700">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FontAwesomeIcon icon={faFont} className="mr-3" />
              Fancy Text Generator
            </h1>
            <p className="opacity-90">
              Generate fancy text with various Unicode styles and decorations
            </p>
          </div>

          {/* Main Content Area */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left Column - Text Input */}
            <div className="flex flex-col h-full">
              <label
                htmlFor="text-input"
                className="block mb-2 font-medium dark:text-gray-300"
              >
                Enter Your Text
              </label>
              <textarea
                id="text-input"
                value={inputText}
                onChange={handleChange}
                className="flex-grow w-full p-4 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Type something..."
                rows={7}
              />
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {characterCount} characters
              </div>
            </div>

            {/* Right Column - Style Selection */}
            <div className="flex flex-col h-full">
              {/* Style Selection */}
              <div className="mb-3">
                <label className="block mb-2 font-medium dark:text-gray-300">
                  Select Style
                </label>
                <button
                  onClick={() => setShowStyleModal(true)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 focus:outline-none"
                >
                  <span>Style {selectedStyle + 1}</span>
                  <Type size={18} />
                </button>
              </div>

              {/* Text Formatting */}
              <div className="flex-grow">
                <h3 className="block mb-3 text-lg font-semibold dark:text-gray-300">
                  Text Formatting
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { key: "bold", label: "Bold", icon: <Bold size={18} /> },
                    {
                      key: "italic",
                      label: "Italic",
                      icon: <Italic size={18} />,
                    },
                    {
                      key: "underline",
                      label: "Underline",
                      icon: <Underline size={18} />,
                    },
                    {
                      key: "uppercase",
                      label: "Uppercase",
                      icon: <ALargeSmall size={18} />,
                    },
                    {
                      key: "lowercase",
                      label: "Lowercase",
                      icon: <ALargeSmall size={18} />,
                    },
                    {
                      key: "capitalize",
                      label: "Title Case",
                      icon: <CaseSensitive size={18} />,
                    },
                  ].map(({ key, label, icon }) => (
                    <button
                      key={key}
                      onClick={() => handleStyleChange(key)}
                      className={`flex p-2 flex-row items-center justify-center gap-1 rounded-lg border-2 transition-all duration-200 ${
                        textStyles[key]
                          ? "bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-500 text-green-700 dark:text-green-400 shadow-sm"
                          : "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                      }`}
                      title={label}
                      aria-label={`Toggle ${label}`}
                    >
                      <div className="text-lg">{icon}</div>
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="p-6 space-y-4">
            {/* Header with actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Left: Preview Title + Length */}
              <div className="flex flex-col">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                  Preview
                </h3>
                {previewText && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {previewText.length} characters
                  </span>
                )}
              </div>

              {/* Right: Buttons Group */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    copyToClipboard(previewText, 0);
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-500 dark:bg-green-600 text-white text-sm font-medium hover:bg-green-600 dark:hover:bg-green-700 transition-colors shadow"
                >
                  <Copy size={16} />
                  {copiedIndex === 0 ? "Copied!" : "Copy Text"}
                </button>

                <button
                  onClick={() => setIsModalOpen(true)}
                  className="px-5 py-2.5 rounded-md bg-green-500 dark:bg-green-600 text-white text-sm font-medium shadow hover:bg-green-600 dark:hover:bg-green-700 transition"
                >
                  Decorate Text
                </button>
              </div>
            </div>

            {/* Preview Box */}
            <div
              className={`w-full min-h-[120px] max-h-64 p-4 rounded-lg border bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 overflow-y-auto custom-scrollbar ${
                !previewText ? "flex items-center justify-center" : ""
              }`}
              style={{
                fontWeight: textStyles.bold ? "bold" : "normal",
                fontStyle: textStyles.italic ? "italic" : "normal",
                textDecoration: textStyles.underline ? "underline" : "none",
                textTransform: textStyles.uppercase
                  ? "uppercase"
                  : textStyles.lowercase
                  ? "lowercase"
                  : textStyles.capitalize
                  ? "capitalize"
                  : "none",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                lineHeight: "1.5",
                color: darkMode ? "#e5e7eb" : "#1f2937",
              }}
            >
              {previewText || (
                <span className="text-gray-400 dark:text-gray-500 text-center">
                  Your fancy text will appear here...
                </span>
              )}
            </div>
          </div>

          {/* Style Modal */}
          {showStyleModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1000]">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl w-[90%] max-w-4xl max-h-[80vh] overflow-hidden flex flex-col text-gray-800 dark:text-gray-200">
                <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 -m-6 mb-4 rounded-t-xl text-white flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Select Style</h3>
                  <button
                    onClick={() => setShowStyleModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="relative mb-4">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    value={styleSearchQuery}
                    onChange={(e) => setStyleSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Search styles..."
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 overflow-y-auto custom-scrollbar">
                  {filteredStyles.length > 0 ? (
                    filteredStyles.map(({ index, preview }) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedStyle(index);
                          setShowStyleModal(false);
                          setStyleSearchQuery("");
                        }}
                        className="p-4 rounded-lg border text-left bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        <div className="text-lg dark:text-gray-200 mb-2">
                          Style {index + 1}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {preview}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                      No styles found matching your search.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title="Decorated Text"
            fancyText={inputText}
            items={decorationItems}
          />
        </article>
      </main>
      <BottomSection toolId={"fancyTextGenerator"} />
    </>
  );
};

export default FancyTextGenerator;